/**
 * Offline mutation queue — persists pending POST/PATCH/PUT/DELETE requests
 * to IndexedDB while the browser is offline and replays them when the
 * `online` event fires.
 *
 * Design:
 *   - Generic: the queue does not know about model or field names. It stores
 *     raw (url, method, headers, body) tuples. This keeps the platform
 *     data-agnostic.
 *   - IndexedDB-first with in-memory fallback when `indexedDB` is missing
 *     (e.g. happy-dom or private mode). Consumers always observe the same
 *     API shape.
 *   - Subscribers receive queue-size updates so UI can show a pending badge.
 */

export interface QueuedMutation {
  id: string;
  url: string;
  method: string;
  headers: Record<string, string>;
  body: string | null;
  createdAt: number;
}

export type QueueListener = (size: number) => void;

const DB_NAME = 'anc-offline';
const DB_VERSION = 1;
const STORE = 'mutations';

type MemoryStore = Map<string, QueuedMutation>;

interface Adapter {
  readonly kind: 'idb' | 'memory';
  add(entry: QueuedMutation): Promise<void>;
  list(): Promise<QueuedMutation[]>;
  remove(id: string): Promise<void>;
  clear(): Promise<void>;
}

let adapterPromise: Promise<Adapter> | null = null;
const listeners = new Set<QueueListener>();

function idbFactory(): IDBFactory | null {
  const g = globalThis as { indexedDB?: IDBFactory };
  return g.indexedDB ?? null;
}

function openIdb(factory: IDBFactory): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = factory.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'id' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

function idbAdapter(db: IDBDatabase): Adapter {
  const run = (
    mode: IDBTransactionMode,
    fn: (store: IDBObjectStore) => void,
  ): Promise<void> =>
    new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      fn(tx.objectStore(STORE));
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });

  return {
    kind: 'idb',
    add: (entry) => run('readwrite', (s) => { s.put(entry); }),
    list: () =>
      new Promise<QueuedMutation[]>((resolve, reject) => {
        const tx = db.transaction(STORE, 'readonly');
        const store = tx.objectStore(STORE);
        const req = store.getAll();
        req.onsuccess = () => {
          const all = req.result as QueuedMutation[];
          all.sort((a, b) => a.createdAt - b.createdAt);
          resolve(all);
        };
        req.onerror = () => reject(req.error);
      }),
    remove: (id) => run('readwrite', (s) => { s.delete(id); }),
    clear: () => run('readwrite', (s) => { s.clear(); }),
  };
}

function memoryAdapter(): Adapter {
  const store: MemoryStore = new Map();
  return {
    kind: 'memory',
    add: async (entry) => {
      store.set(entry.id, entry);
    },
    list: async () =>
      Array.from(store.values()).sort((a, b) => a.createdAt - b.createdAt),
    remove: async (id) => {
      store.delete(id);
    },
    clear: async () => {
      store.clear();
    },
  };
}

async function getAdapter(): Promise<Adapter> {
  if (adapterPromise) return adapterPromise;
  const factory = idbFactory();
  adapterPromise = (async () => {
    if (!factory) return memoryAdapter();
    try {
      const db = await openIdb(factory);
      return idbAdapter(db);
    } catch {
      return memoryAdapter();
    }
  })();
  return adapterPromise;
}

function notify(size: number): void {
  for (const l of listeners) {
    try {
      l(size);
    } catch {
      // listener errors must not affect queue operations
    }
  }
}

async function emitSize(): Promise<void> {
  const size = await queueSize();
  notify(size);
}

/** Reset internal adapter cache — test-only hook. */
export function __resetOfflineQueueForTests(): void {
  adapterPromise = null;
  listeners.clear();
}

export function subscribeQueue(listener: QueueListener): () => void {
  listeners.add(listener);
  // fire initial size asynchronously so subscribers get current count
  void queueSize().then((n) => {
    try {
      listener(n);
    } catch {
      // ignore
    }
  });
  return () => {
    listeners.delete(listener);
  };
}

export async function queueSize(): Promise<number> {
  const a = await getAdapter();
  const all = await a.list();
  return all.length;
}

export async function listQueue(): Promise<QueuedMutation[]> {
  const a = await getAdapter();
  return a.list();
}

function newId(): string {
  const g = globalThis as { crypto?: { randomUUID?: () => string } };
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return `q-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export interface EnqueueInput {
  url: string;
  method: string;
  headers?: Record<string, string>;
  body?: string | null;
}

export async function enqueueMutation(input: EnqueueInput): Promise<QueuedMutation> {
  const entry: QueuedMutation = {
    id: newId(),
    url: input.url,
    method: input.method.toUpperCase(),
    headers: input.headers ?? {},
    body: input.body ?? null,
    createdAt: Date.now(),
  };
  const a = await getAdapter();
  await a.add(entry);
  await emitSize();
  return entry;
}

export async function clearQueue(): Promise<void> {
  const a = await getAdapter();
  await a.clear();
  await emitSize();
}

export interface ReplayResult {
  attempted: number;
  succeeded: number;
  failed: number;
  /**
   * iter-910: count of entries permanently dropped from the queue because the
   * server returned a 4xx — these will not retry on the next reconnect.
   * Always present; older callers that ignore the field still see the
   * established `attempted` / `succeeded` / `failed` shape unchanged.
   * `failed` includes `dropped` (the legacy semantics: a dropped 4xx is also
   * a non-success); a UI that wants to distinguish "still pending" from
   * "gave up" computes `pending = failed - dropped`.
   */
  dropped: number;
}

export type FetchLike = (url: string, init: RequestInit) => Promise<Response>;

/**
 * Replay queued mutations in order.
 *
 * <p>Outcomes:
 * <ul>
 *   <li><b>2xx</b> — succeeded; entry is removed.</li>
 *   <li><b>4xx</b> — permanent client error (validation, auth, not-found,
 *     conflict). The request will never succeed; entry is removed and counted
 *     as failed. iter-885: previously 4xx entries stayed queued forever and
 *     were retried every reconnect, producing perpetual "1 still pending"
 *     toasts the user could never resolve.</li>
 *   <li><b>5xx / network failure</b> — transient; entry stays queued for the
 *     next replay pass.</li>
 * </ul>
 */
export async function replayQueue(
  fetchImpl: FetchLike = (u, i) => fetch(u, i),
): Promise<ReplayResult> {
  const a = await getAdapter();
  const all = await a.list();
  let succeeded = 0;
  let failed = 0;
  let dropped = 0;
  for (const entry of all) {
    try {
      const init: RequestInit = {
        method: entry.method,
        headers: entry.headers,
        credentials: 'include',
      };
      if (entry.body !== null) {
        init.body = entry.body;
      }
      const res = await fetchImpl(entry.url, init);
      if (res.ok) {
        await a.remove(entry.id);
        succeeded++;
      } else if (res.status >= 400 && res.status < 500) {
        // Permanent client error — drop. Replay would never succeed.
        await a.remove(entry.id);
        failed++;
        dropped++;
      } else {
        // Transient (5xx) — keep queued.
        failed++;
      }
    } catch {
      // Network/throw — keep queued.
      failed++;
    }
  }
  await emitSize();
  return { attempted: all.length, succeeded, failed, dropped };
}
